import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, Camera, Trash2, Loader2 } from 'lucide-react';
import { blogAPI } from '../api/blogApi';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { LazyImage } from './LazyImage';

interface BannerUploadProps {
  currentBannerUrl?: string;
  onBannerChange: (url: string) => void;
  onBannerRemove?: () => void;
  /** Blog ID for organized folder structure (blog-{id}/banners/) */
  blogId: number;
  className?: string;
  blogName?: string;
}

// Solid color backgrounds mapped to alphabet - using Tailwind default colors
const SOLID_BACKGROUNDS = [
  'bg-slate-600',    // A
  'bg-gray-600',     // B
  'bg-zinc-600',     // C
  'bg-neutral-600',  // D
  'bg-stone-600',    // E
  'bg-red-600',      // F
  'bg-orange-600',   // G
  'bg-amber-600',    // H
  'bg-yellow-600',   // I
  'bg-lime-600',     // J
  'bg-green-600',    // K
  'bg-emerald-600',  // L
  'bg-teal-600',     // M
  'bg-cyan-600',     // N
  'bg-sky-600',      // O
  'bg-blue-600',     // P
  'bg-indigo-600',   // Q
  'bg-violet-600',   // R
  'bg-purple-600',   // S
  'bg-fuchsia-600',  // T
  'bg-pink-600',     // U
  'bg-rose-600',     // V
  'bg-slate-700',    // W
  'bg-blue-700',     // X
  'bg-green-700',    // Y
  'bg-purple-700',   // Z
];

const getBackgroundFromName = (name?: string): string => {
  if (!name || name.length === 0) {
    return SOLID_BACKGROUNDS[0]; // Default background
  }

  const firstChar = name.charAt(0).toUpperCase();
  const charCode = firstChar.charCodeAt(0);

  // Map A-Z (65-90) to background index
  if (charCode >= 65 && charCode <= 90) {
    return SOLID_BACKGROUNDS[charCode - 65];
  }

  // For non-alphabet characters, use hash of the character
  return SOLID_BACKGROUNDS[charCode % SOLID_BACKGROUNDS.length];
};

export const BannerUpload = ({
  currentBannerUrl,
  onBannerChange,
  onBannerRemove,
  blogId,
  className = '',
  blogName,
}: BannerUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const backgroundClass = getBackgroundFromName(blogName);

  // Handle upload button click - check access first
  const handleUploadClick = () => {
    // If already has access, open file chooser directly
    fileInputRef.current?.click();
  };

  const extractFileId = (url: string): string | null => {
    const match = url.match(/\/d\/([^/?]+)/);
    return match?.[1] || null;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Vui lòng chọn file ảnh');
      return;
    }

    // Validate file size (max 32MB)
    if (file.size > 32 * 1024 * 1024) {
      toast.error('Kích thước ảnh không được vượt quá 32MB');
      return;
    }

    setUploading(true);

    try {
      const result = await blogAPI.admin.uploadBlogMedia(blogId, file, undefined, 'banner');
      const imageUrl = result.data.url;
      onBannerChange(imageUrl);
      if (currentBannerUrl) {
        const previousFileId = extractFileId(currentBannerUrl);
        if (previousFileId && previousFileId !== result.data.fileId) {
          try {
            await blogAPI.admin.deleteBlogMedia(blogId, previousFileId);
          } catch (cleanupError) {
            console.warn('Could not remove previous banner from owner Drive:', cleanupError);
          }
        }
      }
      toast.success('Tải lên ảnh bìa thành công');
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error?.message || 'Không thể tải lên ảnh bìa');
    } finally {
      setUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveClick = () => {
    setShowDeleteDialog(true);
  };

  const confirmRemove = async () => {
    setShowDeleteDialog(false);
    setUploading(true);

    try {
      // Delete through blog-worker so the file is removed from the owner's Drive.
      if (currentBannerUrl) {
        const fileId = extractFileId(currentBannerUrl);
        if (fileId) {
          await blogAPI.admin.deleteBlogMedia(blogId, fileId);
        }
      }

      // Remove from local state
      if (onBannerRemove) {
        onBannerRemove();
      }
      toast.success('Đã xóa ảnh bìa');
    } catch (error: any) {
      console.error('Delete error:', error);
      toast.error('Không thể xóa ảnh bìa khỏi Drive của chủ blog');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={className}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        disabled={uploading}
      />

      <div className="relative">
        {/* Image with lazy loading - fallback to solid color background if no URL */}
        {currentBannerUrl ? (
          <div className="w-full bg-gray-100">
            <LazyImage
              src={currentBannerUrl}
              alt="Ảnh bìa"
              className="block w-full h-auto object-contain"
              skeletonClassName="w-full min-h-64"
            />
          </div>
        ) : (
          <div className={`w-full h-64 md:h-80 ${backgroundClass}`} />
        )}

        {/* Loading overlay */}
        {uploading && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-20">
            <div className="text-center text-white">
              <Loader2 className="h-12 w-12 animate-spin mx-auto mb-2" />
              <p className="text-sm font-medium">Đang tải lên ảnh bìa mới</p>
            </div>
          </div>
        )}

        {/* Upload/Edit button - Icon only at top right corner */}
        <div className="absolute top-4 right-4 z-10">
          {currentBannerUrl ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="icon"
                  variant="secondary"
                  className="shadow-lg bg-white/90 hover:bg-white backdrop-blur-sm h-10 w-10 rounded-full"
                  disabled={uploading}
                  title="Chỉnh sửa ảnh bìa"
                >
                  <Camera className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  onClick={handleUploadClick}
                  disabled={uploading}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Tải ảnh mới lên
                </DropdownMenuItem>
                {onBannerRemove && (
                  <DropdownMenuItem
                    onClick={handleRemoveClick}
                    disabled={uploading}
                    className="text-red-600 focus:text-red-600"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Xóa ảnh bìa
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              variant="secondary"
              size="icon"
              onClick={handleUploadClick}
              disabled={uploading}
              className="shadow-lg bg-white/90 hover:bg-white backdrop-blur-sm h-10 w-10 rounded-full"
              title="Upload ảnh bìa"
            >
              <Upload className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Xác nhận xóa ảnh bìa</AlertDialogTitle>
            <AlertDialogDescription>
              Bạn có chắc chắn muốn xóa ảnh bìa này không? Hành động này không thể hoàn tác.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Hủy</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemove}
              className="bg-red-600 hover:bg-red-700"
            >
              Xóa
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
